import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Checkbox, Input, Select, Textarea } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';

const CODES = [
  'about',
  'contact',
  'shipping',
  'returns',
  'payment',
  'warranty',
  'privacy',
  'cookies',
  'terms',
  'imprint',
  'withdrawal',
] as const;
const MARKETS = ['', 'LV', 'LT', 'EE'] as const;
const LOCALES = ['lv', 'lt', 'et', 'ru', 'en'] as const;

interface Translation {
  title: string;
  body: string;
  seoTitle?: string | null;
  seoDescription?: string | null;
}

interface CmsPage {
  id: string;
  code: string;
  marketCode: string | null;
  isActive: boolean;
  translations: Record<string, Translation>;
}

const empty: Translation = { title: '', body: '', seoTitle: '', seoDescription: '' };

/** Страницы витрины: доставка, возврат, юридические тексты — по языкам и странам. */
export function ContentPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pages = useQuery({
    queryKey: ['content', 'pages'],
    queryFn: () => api<{ items: CmsPage[] }>('/content/pages'),
  });
  const [code, setCode] = useState<string>('shipping');
  const [market, setMarket] = useState<string>('');
  const [locale, setLocale] = useState<string>('lv');
  const [active, setActive] = useState(true);
  const [draft, setDraft] = useState<Record<string, Translation>>({});
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const current = pages.data?.items.find((p) => p.code === code && (p.marketCode ?? '') === market);
  // Форма загружается при выборе страницы или страны — не при обновлении списка после
  // сохранения (иначе сбрасывается сообщение «Сохранено»).
  const key = `${code}|${market}`;
  const [loadedKey, setLoadedKey] = useState<string>();
  useEffect(() => {
    if (!pages.data || loadedKey === key) return;
    setDraft(current?.translations ?? {});
    setActive(current?.isActive ?? true);
    setMessage(undefined);
    setError(undefined);
    setLoadedKey(key);
  }, [pages.data, current, key, loadedKey]);

  const value = draft[locale] ?? empty;
  const set = (key: keyof Translation, v: string) => {
    setDraft((d) => ({ ...d, [locale]: { ...(d[locale] ?? empty), [key]: v } }));
  };
  const save = async () => {
    setError(undefined);
    setMessage(undefined);
    const translations = Object.fromEntries(
      Object.entries(draft)
        .filter(([, v]) => v.title.trim() && v.body.trim())
        .map(([l, v]) => [
          l,
          {
            title: v.title,
            body: v.body,
            ...(v.seoTitle ? { seoTitle: v.seoTitle } : {}),
            ...(v.seoDescription ? { seoDescription: v.seoDescription } : {}),
          },
        ]),
    );
    try {
      await api('/content/pages', {
        method: 'PUT',
        body: { code, marketCode: market || null, isActive: active, translations },
      });
      setMessage(t('common.saved'));
      await queryClient.invalidateQueries({ queryKey: ['content'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      <PageHeader title={t('cms.title')} description={t('cms.hint')} />
      <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
        <Card>
          <CardContent className="grid gap-1 pt-4 text-sm">
            {CODES.map((c) => {
              const published = pages.data?.items.filter((p) => p.code === c && p.isActive) ?? [];
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setCode(c);
                  }}
                  className={`flex items-center justify-between rounded px-2 py-1.5 text-left hover:bg-accent ${c === code ? 'bg-accent font-medium' : ''}`}
                >
                  {t(`cms.codes.${c}`)}
                  {published.length ? <Badge variant="secondary">{published.length}</Badge> : null}
                </button>
              );
            })}
          </CardContent>
        </Card>
        <Card>
          <CardHeader
            title={t(`cms.codes.${code as 'shipping'}`)}
            description={t('cms.formatHint')}
          />
          <CardContent className="grid gap-3 text-sm">
            <div className="flex flex-wrap items-end gap-3">
              <Field label={t('cms.market')}>
                {(id) => (
                  <Select
                    id={id}
                    value={market}
                    onChange={(e) => {
                      setMarket(e.target.value);
                    }}
                  >
                    {MARKETS.map((m) => (
                      <option key={m} value={m}>
                        {m || t('cms.allMarkets')}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <div className="flex gap-1" role="tablist" aria-label={t('cms.language')}>
                {LOCALES.map((l) => (
                  <Button
                    key={l}
                    size="sm"
                    role="tab"
                    aria-selected={l === locale}
                    variant={l === locale ? 'default' : 'outline'}
                    onClick={() => {
                      setLocale(l);
                    }}
                  >
                    {l}
                    {draft[l]?.body ? ' ✓' : ''}
                  </Button>
                ))}
              </div>
              <label className="flex items-center gap-2 pb-2">
                <Checkbox
                  checked={active}
                  onChange={(e) => {
                    setActive(e.target.checked);
                  }}
                />
                {t('cms.published')}
              </label>
            </div>
            <Field label={t('cms.pageTitle')}>
              {(id) => (
                <Input
                  id={id}
                  value={value.title}
                  onChange={(e) => {
                    set('title', e.target.value);
                  }}
                />
              )}
            </Field>
            <Field label={t('cms.body')}>
              {(id) => (
                <Textarea
                  id={id}
                  rows={16}
                  value={value.body}
                  onChange={(e) => {
                    set('body', e.target.value);
                  }}
                />
              )}
            </Field>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label={t('cms.seoTitle')}>
                {(id) => (
                  <Input
                    id={id}
                    value={value.seoTitle ?? ''}
                    onChange={(e) => {
                      set('seoTitle', e.target.value);
                    }}
                  />
                )}
              </Field>
              <Field label={t('cms.seoDescription')}>
                {(id) => (
                  <Input
                    id={id}
                    value={value.seoDescription ?? ''}
                    onChange={(e) => {
                      set('seoDescription', e.target.value);
                    }}
                  />
                )}
              </Field>
            </div>
            <div>
              <Button onClick={() => void save()}>{t('common.save')}</Button>
            </div>
            {message ? <Alert variant="success">{message}</Alert> : null}
            {error ? <Alert variant="destructive">{error}</Alert> : null}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
